import { FunctionComponent, useMemo, useState } from "react"
import { useTypedSelector } from "../../app/store"
import {
  useGetOrganizationResultsAccessStatusQuery,
  useGetOrganizationUserMeasureScoresQuery,
} from "../../app/services/meseeknaApiV2"
import {
  useGetCompositeScoresByOrgIdQuery,
  useGetCompositeScoresWithBucketQuery,
} from "../../app/services/meseeknaApi"
import { useNavigate } from "react-router-dom"
import { skipToken } from "@reduxjs/toolkit/query"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js"
import annotationPlugin from "chartjs-plugin-annotation"
import { Bar } from "react-chartjs-2"
import type { ChartConfiguration } from "chart.js"
import type { AnnotationOptions } from "chartjs-plugin-annotation"

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, annotationPlugin)

const getTierColor = (tierName: string): string => {
  const lower = tierName.toLowerCase()
  if (lower.includes("top")) return "text-green-600"
  if (lower.includes("strong")) return "text-blue-600"
  if (lower.includes("develop")) return "text-yellow-600"
  return "text-red-600"
}

type UserScoreRow = {
  userId: number
  firstName: string
  lastName: string
  email: string
  score: number
  tierName: string
  tierColor: string
}

const BenchmarksSection: FunctionComponent = () => {
  const organization = useTypedSelector((state) => state.authSlice.organization)
  const orgId = Number(organization?.id) || 0
  const navigate = useNavigate()

  const [selectedMeasureId, setSelectedMeasureId] = useState<number | null>(null)

  // Access check
  const { data: accessStatusData, isLoading: isLoadingAccess } =
    useGetOrganizationResultsAccessStatusQuery(orgId, { skip: !orgId })
  const isUnlocked = accessStatusData?.data?.hasAccess ?? false

  // Per-user measure scores
  const { data: scoresData, isLoading: isLoadingScores } =
    useGetOrganizationUserMeasureScoresQuery(orgId, { skip: !orgId || !isUnlocked })

  // Composite score sets for this org (for tier/bucket data)
  const { data: compositeData } = useGetCompositeScoresByOrgIdQuery(
    orgId ? { orgId } : skipToken,
  )

  // Find the active composite set (last one with buckets)
  const compositeSetId = useMemo(() => {
    if (!compositeData?.data?.length) return null
    for (let i = compositeData.data.length - 1; i >= 0; i--) {
      if (compositeData.data[i].buckets_count > 0) {
        return compositeData.data[i].id
      }
    }
    return null
  }, [compositeData])

  // Auto-select first measure from score data
  const measures = useMemo(() => {
    if (!scoresData?.data?.[0]?.measures) return []
    return scoresData.data[0].measures
  }, [scoresData])

  const activeMeasureId = selectedMeasureId ?? measures[0]?.measureId ?? null

  // Fetch tier/bucket data for selected measure + composite set
  const { data: bucketTierData } = useGetCompositeScoresWithBucketQuery(
    compositeSetId && activeMeasureId
      ? { compositeSetId, measureId: activeMeasureId }
      : skipToken,
  )

  // Build chart + table data
  const { chartData, chartOptions, tableRows, selectedMeasureName } = useMemo(() => {
    if (!scoresData?.data || !activeMeasureId) {
      return { chartData: null, chartOptions: null, tableRows: [], selectedMeasureName: "" }
    }

    const users = scoresData.data
    const measure = measures.find((m) => m.measureId === activeMeasureId)
    const measureName = measure?.measureName || ""
    const measureColor = "#6182F7"

    // Collect scores for this measure
    const userScores = users.map((u) => {
      const um = u.measures.find((m) => m.measureId === activeMeasureId)
      return { ...u, score: um?.score || 0 }
    })

    // Build histogram: score → count
    const allScores = userScores.map((u) => u.score)
    const minScore = Math.min(...allScores)
    const maxScore = Math.max(...allScores)

    const labels: string[] = []
    const counts: number[] = []
    const scoreMap = new Map<number, typeof userScores>()

    for (let i = minScore; i <= maxScore; i++) {
      labels.push(i.toString())
      const usersAtScore = userScores.filter((u) => u.score === i)
      counts.push(usersAtScore.length)
      scoreMap.set(i, usersAtScore)
    }

    const maxCount = Math.max(...counts)
    const maxY = Math.ceil((maxCount + 1) / 2) * 2

    // Build tier annotations if bucket data available
    const annotations: Record<string, AnnotationOptions<"box">> = {}
    if (bucketTierData?.data?.length) {
      bucketTierData.data.forEach((tier, idx) => {
        annotations[`tier${idx}`] = {
          type: "box" as const,
          drawTime: "beforeDraw",
          xScaleID: "x",
          yScaleID: "y",
          xMin: tier.bucket[0] - minScore - 0.5,
          xMax: tier.bucket[1] - minScore + 0.5,
          yMin: 0,
          yMax: maxY + 2,
          backgroundColor: tier.color,
          borderWidth: 0,
        }
      })
    }

    const data: ChartConfiguration<"bar">["data"] = {
      labels,
      datasets: [
        {
          data: counts,
          backgroundColor: measureColor,
          borderRadius: 2,
        },
      ],
    }

    const options: ChartConfiguration<"bar">["options"] = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index" as const },
      scales: {
        x: {
          grid: { display: false },
          title: { display: true, text: "Score", font: { size: 12 } },
        },
        y: {
          type: "linear",
          max: maxY + 2,
          title: { display: true, text: "Participants", font: { size: 12 } },
          ticks: { stepSize: 1 },
        },
      },
      plugins: {
        legend: { display: false },
        title: {
          display: true,
          text: measureName,
          font: { size: 18 },
          padding: { bottom: 10 },
        },
        annotation: {
          annotations,
        },
      },
    }

    // Build table rows with tier assignment
    const rows: UserScoreRow[] = userScores.map((u) => {
      let tierName = "—"
      let tierColor = "#9CA3AF"
      if (bucketTierData?.data?.length) {
        const matchedTier = bucketTierData.data.find(
          (t) => u.score >= t.bucket[0] && u.score <= t.bucket[1],
        )
        if (matchedTier) {
          tierName = matchedTier.name
          tierColor = matchedTier.color
        }
      }
      return {
        userId: u.userId,
        firstName: u.firstName || "",
        lastName: u.lastName || "",
        email: u.email,
        score: u.score,
        tierName,
        tierColor,
      }
    })

    rows.sort((a, b) => b.score - a.score)

    return { chartData: data, chartOptions: options, tableRows: rows, selectedMeasureName: measureName }
  }, [scoresData, activeMeasureId, measures, bucketTierData])

  // Loading
  if (isLoadingAccess || (isUnlocked && isLoadingScores)) {
    return (
      <div className="min-h-full bg-gray-50 py-12 px-6">
        <div className="max-w-6xl mx-auto flex justify-center items-center py-24">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4d86ff]"></div>
        </div>
      </div>
    )
  }

  // Locked
  if (!isUnlocked) {
    return (
      <div className="min-h-full bg-gray-50 py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 8v-2a4 4 0 00-8 0v2m-2 0h12a2 2 0 012 2v8a2 2 0 01-2 2H6a2 2 0 01-2-2v-8a2 2 0 012-2z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Data to Compare Yet</h2>
            <p className="text-gray-500 max-w-md mx-auto mb-6">
              After participants complete simulations, compare them against each other and against validated high-performer baselines.
            </p>
            <button
              onClick={() => navigate("/dashboard/recruiter-admin/users/all")}
              className="px-6 py-2.5 text-white text-sm font-medium rounded-lg transition-colors hover:opacity-90"
              style={{ backgroundColor: "#6182F7" }}
            >
              Deploy Simulation
            </button>
          </div>
        </div>
      </div>
    )
  }

  // No data
  if (!measures.length) {
    return (
      <div className="min-h-full bg-gray-50 py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-2xl font-semibold text-gray-900 mb-6">Benchmarks</h1>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <p className="text-gray-500">No simulation data available yet.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-full bg-gray-50 py-8 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Benchmarks</h1>
          <p className="text-sm text-gray-500 mt-1">
            Compare participant scores against performance tiers for each cognitive measure.
          </p>
        </div>

        {/* Measure selector */}
        <div className="mb-4">
          <select
            value={activeMeasureId ?? ""}
            onChange={(e) => setSelectedMeasureId(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-4 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#6182F7] focus:border-transparent"
          >
            {measures.map((m) => (
              <option key={m.measureId} value={m.measureId}>
                {m.measureName}
              </option>
            ))}
          </select>
        </div>

        {/* Tier legend */}
        {bucketTierData?.data?.length ? (
          <div className="flex gap-4 mb-4 flex-wrap">
            {bucketTierData.data.map((tier) => (
              <div key={tier.tiers_id} className="flex items-center gap-2 text-sm">
                <div
                  className="w-3 h-3 rounded-sm"
                  style={{ backgroundColor: tier.color }}
                />
                <span className="text-gray-700">
                  {tier.name} ({tier.bucket[0]}–{tier.bucket[1]})
                </span>
              </div>
            ))}
          </div>
        ) : null}

        {/* Chart */}
        {chartData && chartOptions && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div style={{ height: "380px", width: "100%" }}>
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Name</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Email</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">{selectedMeasureName} Score</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Tier</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row) => (
                  <tr key={row.userId} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {`${row.firstName} ${row.lastName}`.trim() || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{row.email}</td>
                    <td className="text-center px-4 py-3 font-semibold text-gray-900">{row.score}</td>
                    <td className="text-center px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${getTierColor(row.tierName)}`}
                        style={{ backgroundColor: `${row.tierColor}33` }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: row.tierColor }} />
                        {row.tierName}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BenchmarksSection
